import { stockApi } from '$api'
import { IItem, Item } from '$models'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'
import { Dayjs } from 'dayjs'

enum QueryKeys {
  GetItem = 'getItem',
  GetAllItems = 'getAllItems',
}

/**
 * Retrieves all active items, either currently (from/to == undefined) or between a given period
 * @param from the start of the period
 * @param to the end of the period
 * @param ids optional list of item IDs to filter by
 * @returns an list of active items
 */
export const getAllItems = async (
  from?: Dayjs,
  to?: Dayjs,
  ids: string[] | undefined = undefined
): Promise<IItem[]> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return []

  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.GetAllItems, from, to, ids],
    queryFn: async ({ signal }) => {
      return await stockApi.getItems(from, to, ids, signal)
    },
  })
}

/**
 * Retreives an item
 * @param itemId the ID of the item to retreive
 * @param includeDeleted whether to include deleted items
 * @param currentPriceCutoffDate the date to calculate the current price at
 * @returns the item of the given ID or null
 */
export const getItem = async (
  itemId: string,
  includeDeleted?: boolean,
  currentPriceCutoffDate: Dayjs | undefined = undefined
): Promise<IItem | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.GetItem,
      itemId,
      includeDeleted,
      currentPriceCutoffDate,
    ],
    queryFn: async () => {
      return await stockApi.getItem(
        itemId,
        includeDeleted,
        currentPriceCutoffDate
      )
    },
  })
}

/**
 * Creates a new item
 * @param newItem the new item to be created
 * @returns the created item
 */
export const createItem = async (newItem: IItem): Promise<IItem> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return {}

  return await stockApi.createItem(new Item(newItem))
}

/**
 * Updates an item
 * @param item the item to be updated
 * @returns the updated item
 */
export const updateItem = async (
  updatedItem: IItem
): Promise<Item | undefined> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission || !updatedItem.id) return undefined

  return await stockApi.updateItem(updatedItem.id, new Item(updatedItem))
}
